import { cn } from "@faire/prototype-playground/lib/utils";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

export interface MarketplaceBrandTileData {
  token: string;
  name: string;
  coverUrl: string;
  avatarUrl: string;
  rating?: number;
  minOrder: string;
  topShop?: boolean;
  /** Optional badge label shown on the cover (e.g. "New to Faire Source"). */
  badge?: string;
  /** Insider perk line (e.g. "Free shipping over $300"). */
  insiderLine?: string;
  /** Sale/promo line (e.g. "Up to 20% off"). */
  saleLine?: string;
}

export interface MarketplaceBrandTileProps {
  brand: MarketplaceBrandTileData;
  isFollowing?: boolean;
  onFollow?: () => void;
  className?: string;
}

/**
 * Brand recommendation tile ("Brands you might like"): cover image, Top Shop
 * badge, overlapping avatar, name, rating · minimum, and Insider / sale perk
 * lines.
 */
export default function MarketplaceBrandTile({
  brand,
  isFollowing = false,
  onFollow,
  className,
}: MarketplaceBrandTileProps) {
  const cover = usePlaygroundImageWithFallback(brand.coverUrl);
  const avatar = usePlaygroundImageWithFallback(brand.avatarUrl);
  const coverBadgeLabel = brand.badge ?? (brand.topShop ? "Top Shop" : null);

  return (
    <div className={cn("relative flex h-full flex-col", className)}>
      {coverBadgeLabel ? (
        <div className="absolute top-2 left-2 z-3">
          <div className="bg-fs-surface-primary rounded px-2 py-1">
            <p className="type-fs-label-medium text-fs-text-primary">
              {coverBadgeLabel}
            </p>
          </div>
        </div>
      ) : null}

      {/* Favorite */}
      <button
        type="button"
        aria-label={
          isFollowing ? `Following ${brand.name}` : `Follow ${brand.name}`
        }
        onClick={onFollow}
        className="absolute top-2 right-2 z-3 p-1"
      >
        <HeartIcon filled={isFollowing} />
      </button>

      {/* Cover */}
      <div className="rounded-fs-component-default bg-fs-surface-secondary relative aspect-square w-full overflow-hidden">
        <img
          src={cover.src}
          onError={cover.onError}
          alt={brand.name}
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover object-center"
        />
        <div className="bg-fs-action-surface-active/2 pointer-events-none absolute inset-0 z-2" />
      </div>

      {/* Body */}
      <div className="z-1 -mt-5 flex flex-1 flex-col gap-3 px-2">
        <div className="flex flex-col items-start">
          <div className="outline-fs-surface-primary bg-fs-surface-primary rounded-full outline outline-2">
            <img
              src={avatar.src}
              onError={avatar.onError}
              alt=""
              referrerPolicy="no-referrer"
              className="h-10 w-10 rounded-full object-cover"
            />
          </div>
          <p className="type-fs-paragraph-medium mt-1 w-full truncate">
            {brand.name}
          </p>

          <div className="flex flex-row items-center gap-2">
            {brand.rating !== undefined ? (
              <span className="flex flex-row items-center gap-1">
                <StarIcon />
                <span className="type-fs-paragraph">
                  {brand.rating.toFixed(1)}
                </span>
              </span>
            ) : null}
            <span className="type-fs-paragraph">{brand.minOrder}</span>
          </div>

          {brand.insiderLine ? (
            <p
              className="type-fs-paragraph mt-0.5 flex flex-row items-center gap-1 whitespace-nowrap"
              style={{
                color: "var(--slate-color-program-text-insider, #154548)",
              }}
            >
              <InsiderIcon />
              {brand.insiderLine}
            </p>
          ) : null}

          {brand.saleLine ? (
            <p className="type-fs-paragraph text-fs-message-text-sale mt-0.5">
              {brand.saleLine}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill={filled ? "#333333" : "none"}
      stroke="#333333"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 21.343 2.539 12.69C-2.571 7.58 4.949-2.33 12 5.662c7.05-7.99 14.572 1.918 9.461 7.029z" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg
      className="h-3 w-3"
      viewBox="0 0 24 24"
      fill="#333333"
      aria-hidden="true"
    >
      <path d="m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z" />
    </svg>
  );
}

function InsiderIcon() {
  return (
    <svg
      className="relative top-[2px] h-3 w-3"
      viewBox="0 0 16 16"
      fill="#36676a"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.874 8A4.002 4.002 0 0 1 1 7a4 4 0 0 1 7.874-1H15v3.75h-3V8zM5 9.222a2.222 2.222 0 1 0 0-4.444 2.222 2.222 0 0 0 0 4.444"
        fill="currentColor"
      />
    </svg>
  );
}
